import * as React from 'react';
import { Button, Layout, message, Tabs, Tooltip, Upload } from 'antd';
import ConnectionEditorStore from '../stores/ConnectionEditorStore';
import { inject, observer } from 'mobx-react';
import { RulesStores } from '../stores';
import { STORE_RULES_EXTERNAL_CONNECTIONS } from '../constants';
import { HasPermission } from '../../authorization/components/HasPermission';
import { AppPermissions } from '../../authorization/Permissions';
import LayoutHeader from '../../../components/LayoutHeader';
import ConnectionEditDialog from './ConnectionEditDialog';
import ConnectionAddDialog from './ConnectionAddDialog';
import { Utils } from '../../common/services/Utils';
import { UploadChangeParam } from 'antd/lib/upload';
import { ConnectionsDefinitionsVisualStore, IotaConnStores } from '../../iota_connections/stores';
import { STORE_PROJECT_CONNECTIONS_DEFINITIONS } from '../../iota_connections/constants';
import ProjectConnectionsDefinitionsList from '../../iota_connections/components/ProjectConnectionsDefinitionsList';
import RDConnectionsList from './RDConnectionsList';
import { endpoints } from '../../../config';

type Props = RulesStores & IotaConnStores;

type State = {
    activeTab: string;
};

const { TabPane } = Tabs;

export class ExternalConnections extends React.Component<Props, State> {
    private store: ConnectionEditorStore;
    private connDefStore: ConnectionsDefinitionsVisualStore;

    constructor(props: Props) {
        super(props);
        this.store = this.props.RuleExternalConnections!;
        this.connDefStore = this.props[STORE_PROJECT_CONNECTIONS_DEFINITIONS]!;
        this.state = {
            activeTab: 'rd_connections'
        };
    }

    handleTabChange = (activeTab: string) => {
        this.setState({ activeTab });
    };

    render() {
        return (
            <Layout className="screen-size" style={{ ...{ height: '100%', background: 'white' } }}>
                <ConnectionEditDialog store={this.store!} />
                <ConnectionAddDialog store={this.store!} />
                <LayoutHeader
                    subtitle={Utils.getSubtitle(this.store!.currentProject)}
                    title="External Connections"
                    buttons={[
                        <HasPermission
                            entityId={this.store?.currentProject?.id}
                            key="external-connections-import-wrapper"
                            permissionClaim={this.getPermissionClaim()}
                        >
                            <Upload
                                showUploadList={false}
                                className="headerButton"
                                key="file-uploader"
                                name="file"
                                onChange={this.getOnChangeHandler()}
                                action={this.getUploadAction()}
                                headers={this.getUploadHeaders()}
                                beforeUpload={this.getBeforeUploadHandler()}
                            >
                                <span data-id="button-Import" key="import-button-container">
                                    <i className="alpha-icon xs arrow-down" style={{ verticalAlign: 'middle' }} />
                                    <span style={{ marginLeft: 11 }}>Import</span>
                                </span>
                            </Upload>
                        </HasPermission>,
                        <span key="help-message" className="tooltip-container">
                            <Tooltip title={this.getTooltipTitle()}>
                                <i className="alpha-icon lg question-icon" style={{ verticalAlign: 'middle' }} />
                            </Tooltip>
                        </span>,
                        <HasPermission
                            key="rd-connection-add-wrapper"
                            entityId={this.store?.currentProject?.id}
                            permissionClaim={this.getPermissionClaim()}
                        >
                            <Button type="primary" size="large" onClick={this.getButtonClickHandler()}>
                                {this.getButtonLabel()}
                            </Button>
                        </HasPermission>
                    ]}
                />
                <Layout.Content>
                    <Tabs activeKey={this.state.activeTab} onChange={this.handleTabChange} animated={false}>
                        <TabPane tab="RD Connections" key="rd_connections">
                            <RDConnectionsList RuleExternalConnections={this.store} />
                        </TabPane>
                        <TabPane tab="WF Connections" key="wf_connections">
                            <ProjectConnectionsDefinitionsList projectConnectionsDefinitionsUI={this.connDefStore} />
                        </TabPane>
                    </Tabs>
                </Layout.Content>
            </Layout>
        );
    }

    private onChangeRdConnectionFile(info: UploadChangeParam) {
        const status = info.file.status;
        if (status === 'done') {
            message.success(`${info.file.name} file uploaded successfully.`);
            this.store.loadConnections();
        } else if (status === 'error') {
            if (info.file?.response?.status === 409) {
                message.warning(info.file.response.title);
                return;
            }
            message.error(`${info.file.name} file upload failed.`);
        }
    }

    private onChangeWfConnectionFile(info: UploadChangeParam) {
        const status = info.file.status;
        if (status === 'done') {
            message.success(`${info.file.name} file uploaded successfully.`);
            this.connDefStore!.loadDefinitions();
        } else if (status === 'error') {
            if (info.file?.response?.status === 409) {
                message.warning(info.file.response.title);
                return;
            }
            message.error(`${info.file.name} file upload failed.`);
        }
    }

    private showAddConnDefinitionDialog = (): void => {
        this.connDefStore.setCurrent(undefined);
        this.connDefStore.setAddDefinitionDialogVisible(true);
    };

    private getPermissionClaim = (): AppPermissions => {
        return this.state.activeTab === 'rd_connections'
            ? AppPermissions.CanEditRefDataConnections
            : AppPermissions.CanEditIotaConnections;
    };

    private getOnChangeHandler = (): ((info: UploadChangeParam) => void) => {
        return this.state.activeTab === 'rd_connections'
            ? this.onChangeRdConnectionFile.bind(this)
            : this.onChangeWfConnectionFile.bind(this);
    };

    private getUploadAction = (): string => {
        const baseUrl = endpoints.manageUrl;
        const projectId = this.store!.currentProject?.id;
        return this.state.activeTab === 'rd_connections'
            ? `${baseUrl}projects/${projectId}/rules/refData/connections/import`
            : `${baseUrl}projects/${projectId}/iota_connections/import`;
    };

    private getUploadHeaders = (): Record<string, string> => {
        return this.state.activeTab === 'rd_connections'
            ? this.store.fileImportActionHeaders
            : this.connDefStore!.fileImportActionHeaders;
    };

    private getBeforeUploadHandler = (): (() => void) => {
        return this.state.activeTab === 'rd_connections'
            ? () => this.store.setHeaders()
            : () => this.connDefStore.setHeaders();
    };

    private getTooltipTitle = (): string => {
        return this.state.activeTab === 'rd_connections'
            ? 'Reference data connection Management'
            : 'Workflow data connection Management';
    };

    private getButtonLabel = (): string => {
        return this.state.activeTab === 'rd_connections' ? 'Add Reference Data Connection' : 'Add Workflow Connection';
    };

    private getButtonClickHandler = (): (() => void) => {
        return this.state.activeTab === 'rd_connections' ? this.store.add : this.showAddConnDefinitionDialog;
    };
}

export default inject(
    STORE_RULES_EXTERNAL_CONNECTIONS,
    STORE_PROJECT_CONNECTIONS_DEFINITIONS
)(observer(ExternalConnections));
